import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
} from "recharts";
import { ChevronRight, Mic } from "lucide-react";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// FIN-1 — DistribuicaoGastosDonut
// PieChart Recharts interativo. State activeIndex compartilhado entre
// donut e lista lateral (hover/click sincronizam).

const fmtBRL = (v) => Number(v ?? 0).toLocaleString("pt-BR", {
  style: "currency", currency: "BRL", maximumFractionDigits: 0,
});

const COLORS = [
  "#378ADD",  // Moradia (azul)
  "#D85A30",  // Alimentação (laranja)
  "#7F77DD",  // Lazer (roxo)
  "#639922",  // Saúde (verde)
  "#BA7517",  // Transporte (âmbar)
  "#888780",  // Outros (gray)
  "#A78BFA",  // Lavender (extra)
  "#00FFA3",  // brand green (extra)
];

export default function DistribuicaoGastosDonut({ summary, loading, onSeeMore, onTalk }) {
  const byCat = summary?.by_category || {};
  const totalExpense = Object.values(byCat).reduce((s, v) => s + Number(v || 0), 0);

  const items = useMemo(() => {
    const arr = Object.entries(byCat)
      .map(([name, value], i) => ({
        name: name.charAt(0).toUpperCase() + name.slice(1),
        rawName: name,
        value: Number(value || 0),
        color: COLORS[i % COLORS.length],
        pct: totalExpense > 0 ? Math.round((Number(value || 0) / totalExpense) * 100) : 0,
      }))
      .filter(x => x.value > 0)
      .sort((a, b) => b.value - a.value);
    return arr.slice(0, 5);
  }, [byCat, totalExpense]);

  const [activeIndex, setActiveIndex] = useState(0);
  const active = items[activeIndex] || items[0];

  return (
    <Card variant="elevated" className="glass-card glow-hover p-5 md:p-6 flex flex-col" data-testid="distribuicao-donut-card">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <span className="text-caption font-semibold uppercase text-brand-cyan/80" style={{ letterSpacing: "0.12em" }}>
            Distribuição de gastos
          </span>
          <p className="text-micro text-foreground-subtle mt-0.5 uppercase" style={{ letterSpacing: "0.06em" }}>
            Mês atual
          </p>
        </div>
        {active && (
          <p className="text-caption text-foreground-muted text-right">
            <span className="capitalize">{active.name}</span>: <span className="font-semibold text-foreground tabular-nums">{active.pct}%</span>
          </p>
        )}
      </div>

      {loading ? (
        <div className="flex-1 h-[260px] rounded-lg bg-surface-card-hover/40 animate-pulse" />
      ) : items.length === 0 ? (
        <div className="flex-1 flex items-center justify-center py-8 text-center">
          <p className="text-sm text-foreground-muted">Sem despesas registradas neste mês.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 flex-1 min-h-[260px]">
          <div className="relative" data-testid="donut-chart">
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie
                  data={items}
                  innerRadius={60}
                  outerRadius={90}
                  dataKey="value"
                  paddingAngle={2}
                  stroke="none"
                  onMouseEnter={(_, idx) => setActiveIndex(idx)}
                  onClick={(_, idx) => setActiveIndex(idx)}
                >
                  {items.map((it, i) => (
                    <Cell
                      key={i} fill={it.color}
                      opacity={i === activeIndex ? 1 : 0.55}
                      style={{ cursor: "pointer", transition: "opacity 200ms" }}
                    />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(v) => fmtBRL(v)}
                  contentStyle={{
                    backgroundColor: "rgba(8,13,24,0.92)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: 8, fontSize: 11,
                  }}
                  itemStyle={{ color: "#E2E8F0" }}
                />
              </PieChart>
            </ResponsiveContainer>
            {/* Centro: % e label da categoria selecionada */}
            {active && (
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <p className="text-h2 font-bold tabular-nums text-foreground" style={{ color: active.color }}>
                  {active.pct}%
                </p>
                <p className="text-caption text-foreground-muted capitalize">{active.name}</p>
              </div>
            )}
          </div>

          <ul className="space-y-2 self-center" data-testid="donut-list">
            {items.map((it, i) => (
              <li key={it.rawName}>
                <button
                  type="button"
                  onClick={() => setActiveIndex(i)}
                  onMouseEnter={() => setActiveIndex(i)}
                  className={cn(
                    "w-full text-left px-2 py-1.5 rounded-md transition-colors",
                    i === activeIndex ? "bg-surface-card-hover" : "hover:bg-surface-card-hover/60",
                  )}
                  data-testid={`donut-item-${it.rawName}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className="w-2.5 h-2.5 rounded-full shrink-0"
                      style={{ backgroundColor: it.color }}
                      aria-hidden="true"
                    />
                    <span className="text-xs text-foreground capitalize flex-1 truncate">{it.name}</span>
                    <span className="text-micro tabular-nums text-foreground-muted">{it.pct}%</span>
                  </div>
                  <div className="h-1 bg-white/[0.04] rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${it.pct}%` }}
                      transition={{ duration: 0.5, ease: [0.2, 0.8, 0.2, 1] }}
                      className="h-full rounded-full"
                      style={{ backgroundColor: it.color }}
                    />
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-4 pt-3 border-t border-subtle flex items-center justify-between gap-3 flex-wrap">
        <button
          type="button"
          onClick={onSeeMore}
          className="inline-flex items-center gap-1 text-caption text-brand-cyan hover:text-brand-cyan/80 transition-colors"
          data-testid="distribuicao-see-more"
        >
          Ver mais
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
        {active && (
          <Button
            variant="primary-tech"
            size="sm"
            onClick={() => onTalk?.(`Por que ${active.name} foi ${active.pct}% dos meus gastos este mês?`)}
            data-testid="distribuicao-talk"
          >
            <Mic className="w-3.5 h-3.5" />
            Falar com Nexo sobre isso
          </Button>
        )}
      </div>
    </Card>
  );
}
